import { codeOf } from "../../crmpostventa-shared/crmFormat";
import { MOTIVO_LABELS, TIPO_LABELS, motivoLabel, toOptions } from "../../crmpostventa-ipc/ipcFormat";
import { CANAL_OPTIONS } from "../../crmpostventa-ipc/nuevaGestion/nuevaGestionIpcModel";
import { TIPOS_CAMPO } from "../fichaFormat";

// Rules of the Ficha editor (design Prompts 2 and 3; T7 of odd/tasks/ficha-cliente.md, S11). They mirror the server
// rules (FichaReglas, FichaValidaciones, FichaValor) so the asesor sees the same messages and previews before saving;
// the server checks and recomputes everything again. Pure helpers, no data of their own.

export const MENSAJES = {
  cuit: "Revisá el CUIT",
  cbu: "El CBU tiene 22 dígitos",
  mail: "Revisá el mail",
  telefono: "Revisá el teléfono",
  fecha: "Revisá la fecha",
  numero: "Revisá el número",
  motivo: "Escribí el motivo",
};

export const DESCARTAR_TEXTO = "Tenés cambios sin guardar. ¿Descartarlos?";
export const CONFLICTO_TITULO = "Otro asesor modificó esta ficha mientras la editabas";
export const ERROR_GUARDAR_TITULO = "No pudimos guardar los cambios. Probá de nuevo.";
export const AUTO_TAG = "Se completó solo";
export const MOTIVO_MAX = 500;
export const OBSERVACION_MAX = 1000;

// Keys the save answers next to the field keys (FichaService.CampoMotivo / CampoObservacion).
export const CLAVE_MOTIVO = "motivo";
export const CLAVE_OBSERVACION = "observacion";

const TELEFONOS = new Set(["telefono", "telefonoAlternativo"]);

// Read-only fields the server recomputes when the field they come from changes (FichaReglas.AgregarDerivados).
const DERIVADOS = { cbuCvu: "tipoDeCuenta", letraCbu: "nDeCuenta", largoCbu: "nDeCuenta", alicuota: "provincia3" };
// Editable fields that the Rubro fills (código = CodigoActividad, actividad = NomRubro).
const DESDE_RUBRO = ["codActividad", "actividad"];

const texto = (value) => (value === null || value === undefined ? "" : String(value).trim());
const comparable = (value) => texto(value).toLowerCase();

const DIGITOS_CON_SEPARADORES = /^[0-9][0-9\s-]*$/;
const NUMERO = /^[+-]?(\d+([.,]\d+)?|\d{1,3}(\.\d{3})+(,\d+)?|\d{1,3}(,\d{3})+(\.\d+)?)$/;
const MAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const TELEFONO = /^[0-9][0-9 -]*$/;
const CUIT = /^[0-9][0-9\s.-]*$/;
const FECHA = /^\d{4}-\d{2}-\d{2}$/;
const PESOS_CUIT = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

// Official AFIP check digit: 11 - (sum mod 11); 11 means 0 and 10 means the number is invalid.
export const esCuitValido = (value) => {
  const raw = texto(value);
  if (!CUIT.test(raw)) return false;
  const digitos = raw.replace(/\D/g, "");
  if (digitos.length !== 11) return false;
  const suma = PESOS_CUIT.reduce((acc, peso, index) => acc + peso * Number(digitos[index]), 0);
  let verificador = 11 - (suma % 11);
  if (verificador === 11) verificador = 0;
  return verificador !== 10 && verificador === Number(digitos[10]);
};

// Value as the server stores it, to tell a real change: whole numbers lose separators and leading zeros
// (FichaValor.TryNormalizar).
export const normalizar = (campo, value) => {
  const raw = texto(value);
  if (campo?.formato === "ENTERO" && DIGITOS_CON_SEPARADORES.test(raw)) {
    return raw.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  }
  return raw;
};

// ---- Fields of the ficha response, in card order.
export const camposDe = (ficha) =>
  (ficha?.secciones || []).flatMap((seccion) => (seccion.campos || []).map((campo) => ({ ...campo, seccion: campo.seccion || seccion.clave })));

export const originalesDe = (ficha) => Object.fromEntries(camposDe(ficha).map((campo) => [campo.clave, texto(campo.valor)]));

export const isEditable = (campo) => campo?.tipo === TIPOS_CAMPO.EDITABLE;

// ---- Derived previews (FichaReglas.DerivarCbuCvu, DerivarLetraCbu, DerivarLargoCbu, alícuota and Rubro).
export const derivarCbuCvu = (tipoDeCuenta) => {
  const tipo = texto(tipoDeCuenta);
  if (!tipo) return "";
  return tipo.toUpperCase() === "BILLETERA VIRTUAL" ? "CVU" : "CBU";
};

export const derivarLetraCbu = (nDeCuenta) => {
  const cuenta = texto(nDeCuenta);
  if (!cuenta) return "";
  return /^[0-9]+$/.test(cuenta) ? "CORRECTO" : "INCORRECTO";
};

export const derivarLargoCbu = (nDeCuenta) => {
  const cuenta = texto(nDeCuenta);
  return cuenta ? String(cuenta.length) : "";
};

const buscarOpcion = (options, value) => {
  const needle = comparable(value);
  return needle ? (options || []).find((option) => comparable(option?.value) === needle) || null : null;
};

// "3.5%", "4,75 %" → "3.5", "4.75" (FichaValor.ParsearAlicuota + FormatearDecimal); "" when it is not a number.
export const parsearAlicuota = (value) => {
  const raw = texto(value).replace(/%$/, "").replace(/\s/g, "");
  if (!raw) return "";
  const number = Number(/^\d+,\d+$/.test(raw) ? raw.replace(",", ".") : raw);
  return Number.isFinite(number) ? String(number) : "";
};

// The ficha's alicuotasPorProvincia has the source and unit the save derives with (ListaIIBBProvincia, "3.5%" → "3.5");
// the provincias catalog of /api/Provincia divides by 100, so it is only a fallback when the map is missing.
export const derivarAlicuota = (provincia, catalogos) => {
  const mapa = catalogos?.alicuotasPorProvincia;
  if (mapa) {
    const clave = Object.keys(mapa).find((nombre) => comparable(nombre) === comparable(provincia));
    return clave ? parsearAlicuota(mapa[clave]) : "";
  }
  return parsearAlicuota(buscarOpcion(catalogos?.provincias, provincia)?.alicuota);
};

export const derivarDesdeRubro = (rubro, catalogos) => {
  const option = buscarOpcion(catalogos?.rubros, rubro);
  if (!option) return null;
  return {
    codActividad: normalizar({ formato: "ENTERO" }, option.codigoActividad),
    actividad: texto(option.value),
  };
};

const DERIVAR = {
  cbuCvu: (valores) => derivarCbuCvu(valores.tipoDeCuenta),
  letraCbu: (valores) => derivarLetraCbu(valores.nDeCuenta),
  largoCbu: (valores) => derivarLargoCbu(valores.nDeCuenta),
  alicuota: (valores, catalogos) => derivarAlicuota(valores.provincia3, catalogos),
};

/**
 * State of the editor from the original values and the asesor's edits (field key → typed value):
 * valores (what each control shows), automaticos (keys filled by the server rules), manuales (keys the asesor changed
 * by hand), cambios (every change in card order, { campo, antes, ahora, automatico }) and modificados (editable
 * fields that changed, hand-made or filled by the Rubro: the "N campos modificados" count).
 */
export const calcularEstado = (campos, originales, edits, catalogos) => {
  const porClave = new Map(campos.map((campo) => [campo.clave, campo]));
  const valores = { ...originales };
  const manuales = new Set();
  Object.entries(edits).forEach(([clave, value]) => {
    const campo = porClave.get(clave);
    if (!isEditable(campo)) return;
    valores[clave] = value;
    if (normalizar(campo, value) !== normalizar(campo, originales[clave])) manuales.add(clave);
  });

  const automaticos = new Set();
  if (manuales.has("rubro")) {
    const desdeRubro = derivarDesdeRubro(valores.rubro, catalogos);
    DESDE_RUBRO.forEach((clave) => {
      const auto = desdeRubro?.[clave];
      // A value typed by hand in the same edit wins over the Rubro, as on the server.
      if (!auto || !porClave.has(clave) || (manuales.has(clave) && normalizar(porClave.get(clave), edits[clave]) !== auto)) return;
      valores[clave] = auto;
      manuales.delete(clave);
      automaticos.add(clave);
    });
  }

  Object.entries(DERIVADOS).forEach(([clave, origen]) => {
    if (!porClave.has(clave) || !manuales.has(origen)) return;
    valores[clave] = DERIVAR[clave](valores, catalogos);
    automaticos.add(clave);
  });

  const cambios = campos
    .filter((campo) => manuales.has(campo.clave) || automaticos.has(campo.clave))
    .filter((campo) => normalizar(campo, valores[campo.clave]) !== normalizar(campo, originales[campo.clave]))
    .map((campo) => ({
      campo,
      antes: originales[campo.clave] || "",
      ahora: texto(valores[campo.clave]),
      automatico: automaticos.has(campo.clave),
    }));

  return {
    valores,
    manuales,
    automaticos,
    cambios,
    modificados: cambios.filter((cambio) => isEditable(cambio.campo)).map((cambio) => cambio.campo.clave),
  };
};

/**
 * Message of a changed field, null when it is fine. Empty values are valid (a field can be cleared); unchanged values
 * are not checked, so old data does not block other edits (FichaReglas.Validar and ValidarLargoCbu).
 */
export const validarCampo = (campo, value, valores) => {
  const raw = texto(value);
  if (!raw) return null;
  if (campo.clave === "cuitRazonSocial") return esCuitValido(raw) ? null : MENSAJES.cuit;
  if (campo.clave === "mail") return MAIL.test(raw) ? null : MENSAJES.mail;
  if (TELEFONOS.has(campo.clave)) return TELEFONO.test(raw) ? null : MENSAJES.telefono;
  if (campo.clave === "nDeCuenta" && derivarCbuCvu(valores?.tipoDeCuenta) && !/^[0-9]{22}$/.test(raw)) return MENSAJES.cbu;
  if (campo.formato === "ENTERO" || campo.formato === "DECIMAL") return DIGITOS_CON_SEPARADORES.test(raw) || NUMERO.test(raw.replace(/\s/g, "")) ? null : MENSAJES.numero;
  if (campo.formato === "FECHA") return FECHA.test(raw) ? null : MENSAJES.fecha;
  return null;
};

// Values sent to the save: only the fields the asesor changed by hand ("" clears one). The server fills the rest.
export const valoresAEnviar = (estado) => Object.fromEntries([...estado.manuales].map((clave) => [clave, texto(estado.valores[clave])]));

export const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// "Al guardar, la gestión Modificación · Datos bancarios de Kiosco El Sol queda finalizada como Resuelta." (Prompt 3,
// point 2, with FD11: the save finalizes the gestión it came from).
export const gestionRevisionText = (context, cabecera) => {
  const gestion = context?.gestion;
  if (!gestion) return "Al guardar, la gestión queda finalizada como Resuelta.";
  const tipo = TIPO_LABELS[codeOf(gestion.tipo)] || "Gestión";
  const comercio = gestion.nombreComercio || cabecera?.nombreDeFantasia || "";
  return `Al guardar, la gestión ${tipo} · ${motivoLabel(gestion)}${comercio ? ` de ${comercio}` : ""} queda finalizada como Resuelta.`;
};

// ---- "Registrar en Gestiones IPC como gestión finalizada" (informal save; FD3–FD6, FD8; Prompt 3, point 3).
export const TIPO_GESTION_LABEL = TIPO_LABELS.MODIFICACION;
export const MOTIVO_GESTION_OTROS = "OTROS";
// The motives the asesor can pick: the Modificación catalog without Titularidad (a trámite of Altas/Bajas).
export const MOTIVO_GESTION_OPTIONS = toOptions(MOTIVO_LABELS, ["DATOS_BANCARIOS", "DATOS_FISCALES", "DOMICILIO", "DATOS_TERMINAL", MOTIVO_GESTION_OTROS]);
export const CANAL_GESTION_OPTIONS = CANAL_OPTIONS;
export const CANAL_POR_DEFECTO = "OTRO";
const MOTIVO_POR_SECCION = {
  BANCARIO_OPERATIVO: "DATOS_BANCARIOS",
  FISCAL: "DATOS_FISCALES",
  CONTACTO: "DOMICILIO",
  IDENTIFICACION: "DATOS_TERMINAL",
};

// Sections of the fields changed by hand: the ones the Rubro or a derivation filled do not count (FD8, Prompt 6 point 13).
export const seccionesEditadas = (cambios) => [...new Set((cambios || []).filter((cambio) => !cambio.automatico).map((cambio) => cambio.campo.seccion))];

// One edited section gives its motive; more than one (or none) gives Otros, as FichaReglas.CalcularMotivo.
export const motivoGestionAuto = (cambios) => {
  const secciones = seccionesEditadas(cambios);
  return (secciones.length === 1 && MOTIVO_POR_SECCION[secciones[0]]) || MOTIVO_GESTION_OTROS;
};

export const motivoGestionAyuda = (cambios, elegidoAMano) => {
  if (elegidoAMano) return "Elegido a mano";
  return seccionesEditadas(cambios).length === 1 ? "Se completó según la sección editada" : "Se completó según las secciones editadas (más de una)";
};

const toastText = (cabecera) => {
  const terminal = cabecera?.nDeTerminal ? `Terminal ${cabecera.nDeTerminal}` : "";
  return [cabecera?.nombreDeFantasia, terminal].filter(Boolean).join(" · ");
};

// Toast after a save made straight from the Ficha (the gestión one is fichaGestionSavedToast).
export const fichaSavedToast = (cabecera) => ({
  toast: true,
  position: "bottom-end",
  icon: "success",
  title: "Datos actualizados",
  text: toastText(cabecera),
  showConfirmButton: false,
  timer: 4000,
  timerProgressBar: true,
});

// Toast after an informal save that registered its gestión: "Ver gestión" opens it, so it does not close by itself.
export const fichaGestionRegistradaToast = (cabecera) => ({
  toast: true,
  position: "bottom-end",
  icon: "success",
  title: "Datos actualizados · Gestión registrada en Finalizadas",
  text: toastText(cabecera),
  showConfirmButton: true,
  confirmButtonText: "Ver gestión",
  showCloseButton: true,
});

export const GESTION_FINALIZADA_TITULO = "Datos actualizados · Gestión finalizada";

// Only the version conflict asks to reload; any other 409 is a save error with the server message.
export const isConflicto = (error) => error?.status === 409 && String(error?.message || "").trim() === CONFLICTO_TITULO;

// Field errors of a rejected save (400 with errores), split from the motive and observation ones.
export const erroresDeRespuesta = (error) => {
  const errores = error?.status === 400 && error?.payload?.errores && typeof error.payload.errores === "object" ? error.payload.errores : null;
  if (!errores) return null;
  const { [CLAVE_MOTIVO]: motivo = null, [CLAVE_OBSERVACION]: observacion = null, ...campos } = errores;
  return { campos, motivo, observacion };
};
